import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MessageCircle, Search, Sparkles, Send, ExternalLink, Save, Trash2,
  Phone, Star, ShieldCheck, Zap, History, Settings2,
} from "lucide-react";
import WhatsAppPreview from "./WhatsAppPreview";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MODES = [
  { key: "chart", label: "Chart hook" },
  { key: "focus", label: "Life area" },
  { key: "question", label: "Their question" },
];

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const chip = (active) => ({
  padding: "7px 14px", borderRadius: "50px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
  border: active ? "1px solid rgba(37,211,102,0.55)" : "1px solid rgba(255,255,255,0.12)",
  background: active ? "rgba(37,211,102,0.14)" : "transparent",
  color: active ? "#6ee7a8" : "rgba(255,255,255,0.55)",
  transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
});

export default function AdminWhatsApp() {
  const [status, setStatus] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [onlyKundli, setOnlyKundli] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("chart");
  const [focusArea, setFocusArea] = useState("career");
  const [ctas, setCtas] = useState(["ai_chat", "consult", "summary"]);
  const [angle, setAngle] = useState("");
  const [message, setMessage] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [namingTemplate, setNamingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [log, setLog] = useState([]);
  const [senders, setSenders] = useState(null);
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderInput, setSenderInput] = useState("");
  const [contentSid, setContentSid] = useState("");
  const [msgServiceSid, setMsgServiceSid] = useState("");
  const [asTemplate, setAsTemplate] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, cu, tp, lg] = await Promise.all([
        axios.get(`${API}/admin/whatsapp/status`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/customers`, { params: { q: q || undefined, only_kundli: onlyKundli }, withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/log`, { withCredentials: true }),
      ]);
      setStatus(st.data); setCustomers(cu.data.customers || []);
      setTemplates(tp.data.templates || []); setLog(lg.data.messages || []);
      setSenderInput(prev => prev || (st.data.active_sender || "").replace("whatsapp:", ""));
      setContentSid(prev => prev || st.data.content_sid || "");
      setMsgServiceSid(prev => prev || st.data.messaging_service_sid || "");
    } catch (e) {
      setNotice({ type: "error", text: e.response?.data?.detail || "Could not load the WhatsApp console. Refresh or re-verify your admin session." });
    }
  }, [q, onlyKundli]);

  useEffect(() => { load(); }, [load]);

  const toggleCta = (key) => setCtas(c => (c.includes(key) ? c.filter(k => k !== key) : [...c, key]));

  const draft = async () => {
    if (!selected) return;
    setDrafting(true); setNotice(null);
    try {
      const { data } = await axios.post(`${API}/admin/whatsapp/draft`, {
        user_id: selected.id, mode, focus_area: focusArea, ctas, prompt: angle || undefined,
      }, { withCredentials: true });
      setMessage(data.message);
    } catch (e) {
      setNotice({ type: "error", text: e.response?.data?.detail || "Could not draft the message." });
    } finally { setDrafting(false); }
  };

  const send = async (channel) => {
    if (!selected || !message.trim()) return;
    setSending(true); setNotice(null);
    try {
      const { data } = await axios.post(`${API}/admin/whatsapp/send`, {
        user_id: selected.id, message, channel, as_template: channel === "twilio" ? asTemplate : false,
      }, { withCredentials: true });
      if (channel === "link" && data.wa_link) window.open(data.wa_link, "_blank", "noopener");
      setNotice({
        type: "ok",
        text: channel === "twilio" ? `Sent via Twilio (${data.status}).` : "WhatsApp opened with the message ready — tap send there.",
      });
      load();
    } catch (e) {
      setNotice({ type: "error", text: e.response?.data?.detail || "Send failed." });
    } finally { setSending(false); }
  };

  const saveTemplate = async () => {
    // A controlled input instead of window.prompt — prompts are blocked inside WebViews.
    if (!message.trim() || !templateName.trim()) return;
    try {
      await axios.post(`${API}/admin/whatsapp/templates`, { title: templateName.trim(), body: message }, { withCredentials: true });
      setTemplateName(""); setNamingTemplate(false);
      load();
    } catch (e) {
      setNotice({ type: "error", text: e.response?.data?.detail || "Could not save the template." });
    }
  };

  const removeTemplate = async (id) => {
    await axios.delete(`${API}/admin/whatsapp/templates/${id}`, { withCredentials: true }).catch(() => {});
    load();
  };

  const checkSenders = async () => {
    setSenderOpen(true); setSenders(null);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/senders`, { withCredentials: true });
      setSenders(data);
    } catch (e) {
      setSenders({ error: e.response?.data?.detail || "Could not reach Twilio." });
    }
  };

  const pickSender = async (sender_id, extra = {}) => {
    try {
      await axios.post(`${API}/admin/whatsapp/sender`, { sender_id, ...extra }, { withCredentials: true });
      setNotice({ type: "ok", text: `Saved — sending from ${sender_id}.` });
      load(); checkSenders();
    } catch (e) {
      setNotice({ type: "error", text: e.response?.data?.detail || "Could not switch sender." });
    }
  };

  const saveTwilioSetup = () => pickSender(
    (senderInput || status?.active_sender || "").trim(),
    { content_sid: contentSid, messaging_service_sid: msgServiceSid },
  );

  const chartLine = useMemo(() => {
    if (!selected) return "";
    return [selected.lagna && `${selected.lagna} Lagna`, selected.moon_sign && `Moon ${selected.moon_sign}`,
      selected.nakshatra, selected.mahadasha && `${selected.mahadasha} dasha`].filter(Boolean).join(" · ");
  }, [selected]);

  return (
    <div data-testid="admin-whatsapp">
      {/* Header */}
      <div className="glass-card" style={{ padding: "18px 20px", marginBottom: "16px", background: "linear-gradient(135deg, rgba(37,211,102,0.09), rgba(18,140,126,0.05))", border: "1px solid rgba(37,211,102,0.22)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
            <div style={{ width: "38px", height: "38px", borderRadius: "11px", background: "linear-gradient(135deg,#25d366,#128c7e)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MessageCircle size={20} style={{ color: "#04231a" }} />
            </div>
            <div>
              <div className="font-cinzel" style={{ color: "#6ee7a8", fontSize: "1.05rem" }}>WhatsApp Outreach</div>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", marginTop: "2px" }}>
                Pick a customer, let AI write from their Kundli, send with a clear next step.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <span data-testid="wa-status-pill" style={{
              display: "inline-flex", alignItems: "center", gap: "6px", borderRadius: "50px", padding: "5px 12px", fontSize: "0.72rem", fontWeight: 700,
              background: status?.twilio_enabled ? "rgba(34,197,94,0.14)" : "rgba(255,209,102,0.12)",
              border: `1px solid ${status?.twilio_enabled ? "rgba(34,197,94,0.35)" : "rgba(255,209,102,0.3)"}`,
              color: status?.twilio_enabled ? "#86efac" : "#FFD166",
            }}>
              {status?.twilio_enabled
                ? (status.twilio_sandbox
                  ? <><Zap size={12} /> Twilio sandbox sender</>
                  : <><Zap size={12} /> Twilio auto-send live</>)
                : <><ShieldCheck size={12} /> Link mode (tap-to-send)</>}
            </span>
            {status && (
              <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem" }}>
                {status.sent_24h} sent in 24h · {customers.length} reachable
              </span>
            )}
            <button data-testid="wa-check-senders" onClick={checkSenders} className="btn-ghost"
              style={{ padding: "5px 12px", fontSize: "0.72rem", display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <Settings2 size={12} /> Sender setup
            </button>
          </div>
        </div>

        {senderOpen && (
          <div data-testid="wa-sender-panel" style={{ marginTop: "14px", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", marginBottom: "9px" }}>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.8rem" }}>
                Sending from <b data-testid="wa-active-sender" style={{ color: "#6ee7a8" }}>{status?.active_sender || "—"}</b>
              </div>
              <button onClick={() => setSenderOpen(false)} className="btn-ghost" style={{ padding: "3px 10px", fontSize: "0.7rem" }}>Hide</button>
            </div>
            {!senders ? (
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.76rem" }}>Asking Twilio which senders are registered…</p>
            ) : senders.error ? (
              <p data-testid="wa-sender-error" style={{ color: "#fca5a5", fontSize: "0.76rem" }}>{senders.error}</p>
            ) : (
              <>
                <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "8px" }}>
                  {(senders.whatsapp_senders || []).length === 0 && (
                    <span data-testid="wa-no-senders" style={{ color: "#fca5a5", fontSize: "0.76rem" }}>
                      This Twilio account has no registered WhatsApp sender yet.
                    </span>
                  )}
                  {(senders.whatsapp_senders || []).map((s, i) => (
                    <button key={s.sender_id} data-testid={`wa-sender-${i}`} onClick={() => pickSender(s.sender_id)}
                      style={chip(status?.active_sender === s.sender_id)}>
                      {s.sender_id}{s.status ? ` · ${s.status.toLowerCase()}` : ""}
                    </button>
                  ))}
                </div>
                {status?.active_sender && (senders.whatsapp_senders || []).length > 0
                  && !(senders.whatsapp_senders || []).some(s => s.sender_id === status.active_sender) && (
                  <p data-testid="wa-sender-mismatch" style={{ color: "#FFD166", fontSize: "0.74rem", lineHeight: 1.55 }}>
                    <b>{status.active_sender}</b> is not a registered WhatsApp sender on this Twilio account, so automatic
                    sends will fail with Twilio error 63007. Register that number as a WhatsApp Sender in the Twilio console
                    (Messaging → Senders → WhatsApp senders, needs Meta Business verification and the number must not be
                    active on the normal WhatsApp app), or pick one of the senders above. “Open in WhatsApp” keeps working
                    from your own phone either way.
                  </p>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "8px", marginTop: "12px" }}>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", marginBottom: "4px" }}>Send from (WhatsApp sender)</div>
                    <input data-testid="wa-sender-input" value={senderInput} onChange={e => setSenderInput(e.target.value)}
                      placeholder="+918145822791" className="cosmic-input" style={{ fontSize: "0.78rem", padding: "8px 11px" }} />
                  </div>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", marginBottom: "4px" }}>Approved template SID (optional)</div>
                    <input data-testid="wa-content-sid" value={contentSid} onChange={e => setContentSid(e.target.value)}
                      placeholder="HX…" className="cosmic-input" style={{ fontSize: "0.78rem", padding: "8px 11px" }} />
                  </div>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", marginBottom: "4px" }}>Messaging service SID (optional)</div>
                    <input data-testid="wa-msg-service-sid" value={msgServiceSid} onChange={e => setMsgServiceSid(e.target.value)}
                      placeholder="MG…" className="cosmic-input" style={{ fontSize: "0.78rem", padding: "8px 11px" }} />
                  </div>
                </div>
                <button data-testid="wa-save-sender" onClick={saveTwilioSetup} className="btn-ghost"
                  style={{ marginTop: "9px", padding: "7px 14px", fontSize: "0.76rem" }}>Save Twilio setup</button>
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem", marginTop: "8px", lineHeight: 1.5 }}>
                  WhatsApp only allows free-form text within 24h of the customer's last message. Save an approved
                  template SID and we'll fall back to it automatically when that window has closed.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="wa-grid" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "16px", alignItems: "start" }}>
        {/* Customers */}
        <div className="glass-card" style={{ padding: "14px" }}>
          <div style={{ position: "relative", marginBottom: "10px" }}>
            <Search size={14} style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />
            <input data-testid="wa-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, email"
              className="cosmic-input" style={{ paddingLeft: "32px", fontSize: "0.82rem" }} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "7px", color: "rgba(255,255,255,0.5)", fontSize: "0.76rem", marginBottom: "10px", cursor: "pointer" }}>
            <input data-testid="wa-only-kundli" type="checkbox" checked={onlyKundli} onChange={e => setOnlyKundli(e.target.checked)} />
            Only customers with a Kundli
          </label>
          <div style={{ maxHeight: "540px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "7px" }}>
            {customers.length === 0 && (
              <p data-testid="wa-empty" style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", padding: "12px 4px" }}>
                No customers with a phone number yet.
              </p>
            )}
            {customers.map((c, i) => {
              const active = selected?.id === c.id;
              return (
                <button key={c.id} data-testid={`wa-customer-${i}`} onClick={() => { setSelected(c); setMessage(""); setNotice(null); }}
                  style={{
                    textAlign: "left", cursor: "pointer", borderRadius: "11px", padding: "10px 11px",
                    border: active ? "1px solid rgba(37,211,102,0.5)" : "1px solid rgba(255,255,255,0.07)",
                    background: active ? "rgba(37,211,102,0.1)" : "rgba(255,255,255,0.02)",
                    transition: "background-color 0.2s ease, border-color 0.2s ease",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                    <span style={{ color: "white", fontSize: "0.84rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name || "Unnamed"}
                    </span>
                    {c.has_kundli && <Star size={11} style={{ color: "#FFD166", flexShrink: 0 }} fill="#FFD166" />}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "3px" }}>
                    <Phone size={10} /> +{c.wa_number}
                    {c.plan && c.plan !== "free" && <span style={{ color: "#c084fc" }}>· {c.plan}</span>}
                  </div>
                  {(c.lagna || c.moon_sign) && (
                    <div style={{ color: "rgba(110,231,168,0.75)", fontSize: "0.69rem", marginTop: "3px" }}>
                      {[c.lagna && `${c.lagna} Lagna`, c.moon_sign && `Moon ${c.moon_sign}`].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {c.last_whatsapp_at && (
                    <div style={{ color: "rgba(255,255,255,0.28)", fontSize: "0.66rem", marginTop: "3px" }}>
                      messaged {ago(c.last_whatsapp_at)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Composer */}
        <div className="glass-card" style={{ padding: "18px" }}>
          {!selected ? (
            <div data-testid="wa-no-selection" style={{ textAlign: "center", padding: "50px 20px", color: "rgba(255,255,255,0.4)" }}>
              <MessageCircle size={30} style={{ color: "rgba(37,211,102,0.4)", marginBottom: "12px" }} />
              <p style={{ fontSize: "0.88rem" }}>Pick a customer on the left to start a personalised WhatsApp message.</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: "16px" }}>
                <div style={{ color: "white", fontSize: "1rem", fontWeight: 700 }}>{selected.name || "Unnamed"}</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", marginTop: "3px" }}>
                  +{selected.wa_number}{selected.birth_city ? ` · born ${selected.birth_city}` : ""}
                </div>
                {chartLine && (
                  <div data-testid="wa-chart-line" style={{ color: "#6ee7a8", fontSize: "0.76rem", marginTop: "5px" }}>✦ {chartLine}</div>
                )}
                {!selected.has_kundli && (
                  <div data-testid="wa-no-kundli-note" style={{ color: "#FFD166", fontSize: "0.74rem", marginTop: "6px" }}>
                    No Kundli on file — the message will invite them to create one.
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "12px" }}>
                {MODES.map(m => (
                  <button key={m.key} data-testid={`wa-mode-${m.key}`} onClick={() => setMode(m.key)} style={chip(mode === m.key)}>
                    {m.label}
                  </button>
                ))}
              </div>

              {mode === "focus" && (
                <select data-testid="wa-focus-area" value={focusArea} onChange={e => setFocusArea(e.target.value)}
                  className="cosmic-input" style={{ marginBottom: "12px", fontSize: "0.84rem", textTransform: "capitalize" }}>
                  {(status?.focus_areas || ["career"]).map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              )}

              <div style={{ marginBottom: "12px" }}>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.74rem", marginBottom: "7px" }}>Next steps to include</div>
                <div style={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
                  {(status?.ctas || []).map(c => (
                    <button key={c.key} data-testid={`wa-cta-${c.key}`} onClick={() => toggleCta(c.key)} style={chip(ctas.includes(c.key))}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <input data-testid="wa-angle" value={angle} onChange={e => setAngle(e.target.value)}
                placeholder="Optional: your angle, e.g. 'mention the Saturn transit before Diwali'"
                className="cosmic-input" style={{ fontSize: "0.82rem", marginBottom: "12px" }} />

              <button data-testid="wa-generate-btn" onClick={draft} disabled={drafting} className="btn-gold"
                style={{ width: "100%", padding: "11px", fontSize: "0.86rem", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: drafting ? 0.6 : 1 }}>
                <Sparkles size={15} /> {drafting ? "Reading their chart…" : "Write it for me"}
              </button>

              <textarea data-testid="wa-message" value={message} onChange={e => setMessage(e.target.value)} rows={8}
                placeholder="Your message — generate it above or write your own."
                className="cosmic-input" style={{ marginTop: "12px", fontSize: "0.86rem", lineHeight: 1.6, resize: "vertical", minHeight: "150px" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", flexWrap: "wrap", gap: "8px" }}>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem" }}>{message.length} characters</span>
                <button data-testid="wa-save-template" onClick={() => setNamingTemplate(v => !v)} className="btn-ghost"
                  style={{ padding: "5px 11px", fontSize: "0.72rem", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                  <Save size={11} /> Save as template
                </button>
              </div>

              {namingTemplate && (
                <div style={{ display: "flex", gap: "7px", marginTop: "9px", flexWrap: "wrap" }}>
                  <input data-testid="wa-template-name" value={templateName} onChange={e => setTemplateName(e.target.value)}
                    placeholder="Template name, e.g. Diwali nudge" className="cosmic-input"
                    style={{ flex: "1 1 200px", fontSize: "0.8rem", padding: "9px 12px" }} />
                  <button data-testid="wa-template-save-confirm" onClick={saveTemplate} disabled={!templateName.trim()} className="btn-ghost"
                    style={{ padding: "8px 14px", fontSize: "0.76rem", opacity: templateName.trim() ? 1 : 0.5 }}>Save</button>
                  <button data-testid="wa-template-save-cancel" onClick={() => { setNamingTemplate(false); setTemplateName(""); }} className="btn-ghost"
                    style={{ padding: "8px 12px", fontSize: "0.76rem", color: "rgba(255,255,255,0.45)" }}>Cancel</button>
                </div>
              )}

              {templates.length > 0 && (
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "10px" }}>
                  {templates.map((tpl, i) => (
                    <span key={tpl.id} style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50px", padding: "4px 6px 4px 11px", fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>
                      <button data-testid={`wa-template-${i}`} onClick={() => setMessage(tpl.body)}
                        style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "0.72rem" }}>
                        {tpl.title}
                      </button>
                      <button data-testid={`wa-template-delete-${i}`} onClick={() => removeTemplate(tpl.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(252,165,165,0.8)", display: "inline-flex" }}>
                        <Trash2 size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div style={{ marginTop: "16px" }}>
                <WhatsAppPreview message={message} name={selected.name} />
              </div>

              {notice && (
                <div data-testid={`wa-notice-${notice.type}`} style={{
                  marginTop: "12px", padding: "10px 12px", borderRadius: "10px", fontSize: "0.8rem", lineHeight: 1.5,
                  background: notice.type === "ok" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                  color: notice.type === "ok" ? "#86efac" : "#fca5a5",
                }}>
                  {notice.text}
                </div>
              )}

              <div style={{ display: "flex", gap: "9px", marginTop: "14px", flexWrap: "wrap" }}>
                <button data-testid="wa-open-btn" onClick={() => send("link")} disabled={sending || !message.trim()}
                  style={{
                    flex: "1 1 190px", padding: "12px", borderRadius: "50px", border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg,#25d366,#128c7e)", color: "#04231a", fontWeight: 800, fontSize: "0.86rem",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    opacity: (sending || !message.trim()) ? 0.5 : 1,
                  }}>
                  <ExternalLink size={15} /> Open in WhatsApp
                </button>
                {status?.twilio_enabled && (
                  <button data-testid="wa-twilio-btn" onClick={() => send("twilio")} disabled={sending || !message.trim()} className="btn-purple"
                    style={{ flex: "1 1 170px", padding: "12px", fontSize: "0.86rem", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: (sending || !message.trim()) ? 0.5 : 1 }}>
                    <Send size={14} /> {sending ? "Sending…" : "Send automatically"}
                  </button>
                )}
              </div>
              {status?.twilio_enabled && status?.content_sid && (
                <label style={{ display: "inline-flex", alignItems: "center", gap: "7px", marginTop: "9px", color: "rgba(255,255,255,0.5)", fontSize: "0.74rem", cursor: "pointer" }}>
                  <input data-testid="wa-as-template" type="checkbox" checked={asTemplate} onChange={e => setAsTemplate(e.target.checked)} />
                  Send as the approved template (needed outside the 24h window)
                </label>
              )}
              {status?.twilio_sandbox && (
                <p data-testid="wa-sandbox-note" style={{ color: "rgba(255,209,102,0.7)", fontSize: "0.72rem", marginTop: "9px", lineHeight: 1.5 }}>
                  Automatic sending currently uses Twilio's shared sandbox number, which only reaches customers
                  who messaged “join …” to it first. “Open in WhatsApp” always works — swap in your own approved
                  WhatsApp sender in <code>TWILIO_WHATSAPP_FROM</code> to send to anyone automatically.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Log */}
      <div className="glass-card" style={{ padding: "16px", marginTop: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <History size={15} style={{ color: "#6ee7a8" }} />
          <div style={{ color: "white", fontSize: "0.9rem", fontWeight: 700 }}>Recent outreach</div>
        </div>
        {log.length === 0 ? (
          <p data-testid="wa-log-empty" style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem" }}>Nothing sent yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            {log.map((m, i) => (
              <div key={m.id} data-testid={`wa-log-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: "10px", padding: "9px 11px", background: "rgba(255,255,255,0.02)", borderRadius: "9px", flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "white", fontSize: "0.82rem" }}>{m.name || "Unnamed"} <span style={{ color: "rgba(255,255,255,0.35)" }}>+{m.phone}</span></div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "540px" }}>{m.preview}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ color: (m.status === "failed" || m.status === "undelivered") ? "#fca5a5" : m.status === "delivered" || m.status === "read" ? "#86efac" : "#FFD166", fontSize: "0.72rem", fontWeight: 700 }}>
                    {m.channel === "twilio" ? (m.template_used ? "Twilio · template" : "Twilio") : "Link"} · {m.status}
                  </div>
                  {m.error && <div style={{ color: "rgba(252,165,165,0.8)", fontSize: "0.66rem", maxWidth: "320px" }}>{m.error}</div>}
                  <div style={{ color: "rgba(255,255,255,0.28)", fontSize: "0.68rem" }}>{ago(m.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
